/**
 * Photo Domain Service
 *
 * Domain service for photo URL retrieval operations in the BiteWise healthcare provider platform.
 * Handles photo URL fetching with role-based access control and enhanced error handling.
 *
 * @module services/domains/PhotoDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager } from '../shared';

/**
 * Photo URLs request interface
 */
export interface GetPhotoUrlsRequest {
  user_ids: string[];
}

/**
 * Photo URLs response interface
 */
export interface GetPhotoUrlsResponse {
  photo_urls: Record<string, string | null>;
}

/**
 * Photo URLs API request interface
 */
interface GetPhotoUrlsApiRequest {
  user_ids: string[];
}

/**
 * Photo URLs API response interface
 */
interface GetPhotoUrlsApiResponse {
  photo_urls: Record<string, string | null>;
}

/**
 * Photo domain-specific error class
 */
export class PhotoApiError extends BaseApiError {
  /** The operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'PhotoApiError';
    this.operation = operation;
  }
}

/**
 * Photo API endpoints
 */
const ENDPOINTS = {
  PHOTO_URLS: '/photos/urls',
} as const;

/**
 * Photo Domain Service
 *
 * Provides photo URL retrieval operations with enhanced error handling,
 * retry logic, and request deduplication.
 */
export class PhotoDomain extends BaseDomain<PhotoApiError> {
  constructor() {
    const apiClient = new ApiClient<PhotoApiError>(
      (message, status, fieldErrors, code) => new PhotoApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'Photo';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): PhotoApiError {
    return new PhotoApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Get photo URLs for multiple users
   *
   * Retrieves photo URLs for multiple users by their IDs with role-based access control.
   * Users can only access photos they have permission to view based on their role.
   *
   * @param request Photo URLs request parameters
   * @returns Promise<GetPhotoUrlsResponse> Photo URLs mapped by user ID
   * @throws PhotoApiError When retrieval fails
   */
  async getPhotoUrls(request: GetPhotoUrlsRequest): Promise<GetPhotoUrlsResponse> {
    return this.executeWithDeduplication(`getPhotoUrls-${request.user_ids.sort().join(',')}`, async () => {
      return this.executeWithRetry(async () => {
        try {
          // Validate request
          if (!request.user_ids || request.user_ids.length === 0) {
            throw this.createDomainError(
              'getPhotoUrls',
              'At least one user ID is required',
              400
            );
          }

          if (request.user_ids.length > 100) {
            throw this.createDomainError(
              'getPhotoUrls',
              'Maximum 100 user IDs allowed per request',
              400
            );
          }

          // Validate UUIDs
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          const invalidIds = request.user_ids.filter(id => !uuidRegex.test(id));
          if (invalidIds.length > 0) {
            throw this.createDomainError(
              'getPhotoUrls',
              `Invalid UUID format for user IDs: ${invalidIds.join(', ')}`,
              400
            );
          }

          const response = await this.apiClient.post<GetPhotoUrlsApiResponse>(
            ENDPOINTS.PHOTO_URLS,
            this.transformGetPhotoUrlsRequest(request)
          );

          return this.transformGetPhotoUrlsResponse(response);
        } catch (error) {
          if (error instanceof PhotoApiError) {
            throw error;
          }

          throw this.createDomainError(
            'getPhotoUrls',
            `Failed to retrieve photo URLs: ${error instanceof Error ? error.message : 'Unknown error'}`,
            error instanceof BaseApiError ? error.status : undefined,
            error instanceof BaseApiError ? error.fieldErrors : undefined
          );
        }
      });
    });
  }

  /**
   * Transform photo URLs request from frontend format to API format
   */
  private transformGetPhotoUrlsRequest(request: GetPhotoUrlsRequest): GetPhotoUrlsApiRequest {
    return {
      user_ids: request.user_ids
    };
  }

  /**
   * Transform photo URLs response from API format to frontend format
   */
  private transformGetPhotoUrlsResponse(apiResponse: GetPhotoUrlsApiResponse): GetPhotoUrlsResponse {
    return {
      photo_urls: apiResponse.photo_urls
    };
  }
}

/**
 * Singleton instance of PhotoDomain
 */
export const photoDomain = new PhotoDomain();